import { UserProfile } from "@/types/user";
import { teamsService } from "@/services/teamsService";
import { toast } from "sonner";

export interface UserProfilePulse {
  id: string;
  name: string;
  email?: string;
  avatarUrl?: string;
  bio?: string;
  role: string;
  status: string;
  checkInStreak: number;
  lastCheckIn: string;
  lastReflection: string | null;
}

/**
 * Fetch team members (basic profile data)
 */
export const fetchTeamMembers = async (
  teamId: string
): Promise<UserProfile[]> => {
  try {
    const response = await teamsService.getMembers(teamId);
    
    // API returns data directly
    const members = Array.isArray(response) ? response : (response?.members || []);

    // Map backend response to UserProfile format
    return members.map((member: any) => ({
      id: member.id,
      name: member.name || "",
      email: member.email,
      avatarUrl: member.avatarUrl,
      bio: member.bio,
      role: member.isAdmin ? "manager" : "individual",
      teamType: "general",
      skills: [],
    }));
  } catch (error) {
    console.error("Failed to fetch team members", error);
    return [];
  }
};

/**
 * Fetch team members with pulse data (check-in status, streaks, etc.)
 * Now uses backend API that implements the exact same logic as before
 */
export const fetchTeamMembersPulse = async (
  teamId: string,
  isAdmin: boolean = false
): Promise<UserProfilePulse[]> => {
  try {
    console.log("fetchTeamMembersPulse called with teamId:", teamId);
    
    // Validate team ID format
    if (!teamId || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(teamId)) {
      console.error("Invalid team ID format in fetchTeamMembersPulse:", teamId);
      return [];
    }

    const response = await teamsService.getMembersPulse(teamId);
    
    // API returns data directly
    return Array.isArray(response) ? response : (response?.members || []);
  } catch (error) {
    console.error("Failed to fetch team members pulse:", error);
    return [];
  }
};

/**
 * Fetch action suggestions (now from insights API)
 */
export const fetchActionSuggestions = async (
  userId: string
): Promise<any | null> => {
  try {
    // This is now handled by the insights service
    const insightsService = await import("@/services/insightsService");
    const data = await insightsService.insightsService.getSuggestions();
    
    // API returns data directly
    return data || null;
  } catch (error) {
    console.error("Error fetching action suggestions:", error);
    return null;
  }
};

interface PulseDataPoint {
  name: string;
  team: number;
  personal: number | null;
}

/**
 * Fetch team pulse data (energy trends over last 5 days)
 */
export const fetchTeamPulseData = async (
  teamId: string,
  userId: string
): Promise<PulseDataPoint[]> => {
  try {
    const response = await teamsService.getStats(teamId, { 
      type: 'pulse',
      days: 5,
      userId 
    });
    
    // API returns data directly
    return Array.isArray(response) ? response : (response?.data || []);
  } catch (error) {
    console.error("Error fetching team pulse data:", error);
    return [];
  }
};

interface TeamEnergyStats {
  currentAverage: number;
  previousAverage: number;
  percentageChange: number;
  message: string;
}

/**
 * Fetch team energy statistics (current vs previous week)
 */
export const fetchTeamEnergyStats = async (
  teamId: string
): Promise<TeamEnergyStats> => {
  const defaultStats = {
    currentAverage: 0,
    previousAverage: 0,
    percentageChange: 0,
    message: "No data available",
  };
  
  try {
    const response = await teamsService.getStats(teamId, { 
      type: 'energy' 
    });
    
    // API returns data directly
    if (!response) return defaultStats;
    
    return {
      currentAverage: response.currentAverage || 0,
      previousAverage: response.previousAverage || 0,
      percentageChange: response.percentageChange || 0,
      message: response.message || "No data available",
    };
  } catch (error) {
    console.error("Error fetching team energy stats:", error);
    return { ...defaultStats, message: "Error calculating team energy" };
  }
};

interface TeamParticipationStats {
  participationRate: number;
  activeMembers: number;
  totalMembers: number;
  message: string;
}

/**
 * Fetch team participation statistics (last 3 days)
 */
export const fetchTeamParticipationStats = async (
  teamId: string
): Promise<TeamParticipationStats> => {
  const defaultStats = {
    participationRate: 0,
    activeMembers: 0,
    totalMembers: 0,
    message: "No data available",
  };
  
  try {
    const response = await teamsService.getStats(teamId, { 
      type: 'participation' 
    });
    
    // API returns data directly
    if (!response) return defaultStats;
    
    return {
      participationRate: response.participationRate || 0,
      activeMembers: response.activeMembers || 0,
      totalMembers: response.totalMembers || 0,
      message: response.message || "No data available",
    };
  } catch (error) {
    console.error("Error fetching team participation stats:", error);
    return { ...defaultStats, message: "Error calculating participation rate" };
  }
};

interface TeamMoodStats {
  good: number;
  okay: number;
  low: number;
}

/**
 * Fetch team mood distribution (last 3 days)
 */
export const fetchTeamMoodStats = async (
  teamId: string
): Promise<TeamMoodStats> => {
  const defaultStats = {
    good: 0,
    okay: 0,
    low: 0,
  };
  
  try {
    const response = await teamsService.getStats(teamId, { 
      type: 'mood' 
    });
    
    // API returns data directly
    if (!response) return defaultStats;
    
    return {
      good: response.good || 0,
      okay: response.okay || 0,
      low: response.low || 0,
    };
  } catch (error) {
    console.error("Error fetching team mood stats:", error);
    return defaultStats;
  }
};

/**
 * Fetch all teams (optionally filtered by organization)
 */
export const fetchTeams = async (
  organizationId?: string
): Promise<any[] | null> => {
  try {
    const response = await teamsService.getTeams();
    
    // API returns data directly
    const teams = Array.isArray(response) ? response : (response?.teams || []);

    console.log("Teams loaded successfully:", teams);
    
    // Filter by organization if provided
    if (organizationId && teams.length > 0) {
      return teams.filter((team: any) => team.organizationId === organizationId);
    }
    
    return teams;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn("Exception while loading teams:", error);
    }
    return [];
  }
};

/**
 * Fetch teams for the current user's organization
 */
export const fetchUserOrganizationTeams = async (
  userId: string
): Promise<any[] | null> => {
  try {
    // Backend will automatically filter by user's organization
    const response = await teamsService.getUserTeams();
    
    // API returns data directly
    const teams = Array.isArray(response) ? response : (response?.teams || []);

    if (teams.length === 0) {
      console.log("User has no organization teams, showing all teams");
      return fetchTeams();
    }

    return teams;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn("Exception while loading user organization teams:", error);
    }
    return [];
  }
};

/**
 * Fetch detailed user information for team members
 */
export const fetchUserTeamDetails = async (
  teamId: string
): Promise<UserProfile[]> => {
  try {
    if (process.env.NODE_ENV === 'development') {
      console.log("fetchUserTeamDetails called with teamId:", teamId);
    }
    
    // Validate team ID format
    if (!teamId || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(teamId)) {
      if (process.env.NODE_ENV === 'development') {
        console.warn("Invalid team ID format:", teamId);
      }
      return [];
    }
    
    const response = await teamsService.getMembers(teamId);
    
    // API returns data directly
    const members = Array.isArray(response) ? response : (response?.members || []);
    
    if (members.length === 0) {
      console.log("No team members found for team:", teamId);
      return [];
    }

    // Get team info
    const teamData = await teamsService.getTeamById(teamId);

    // Transform the data into UserProfile format
    const transformedData: UserProfile[] = members.map((member: any) => ({
      id: member.id,
      name: member.name || "",
      role: member.isAdmin ? "manager" : "individual",
      teamType: teamData?.type as any || "general",
      skills: [],
      email: member.email || "",
      avatarUrl: member.avatarUrl || undefined,
      bio: member.bio || undefined,
      persona: undefined,
      teamId: teamId,
      teamName: teamData?.name || "Unknown Team",
      isNewUser: undefined,
      joinedDate: member.joinedAt,
      openToHelp: undefined,
      preferredWorkingHours: undefined,
      growthGoals: undefined,
      about: undefined,
      contactInfo: undefined,
    }));

    console.log("Team details loaded successfully:", transformedData);
    return transformedData;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn("Exception while loading team details:", error);
    }
    return [];
  }
};
